// ═══════════════════════════════════════════════════════════════════════════════
// Subscription terms modal — content from legal_documents.subscription_terms
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useId, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import {
  resolveLegalDocumentLocale,
  usesEnglishLegalContent,
  type LegalDocumentPublic,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { legalDocumentApi } from '../../lib/api';
import { MODAL_BACKDROP_CENTER_CLASS, MODAL_BACKDROP_SCRIM_CLASS } from '../../lib/modalClasses';
import { ModalCircleCloseButton } from '../ui/ModalCircleCloseButton';

type SubscriptionTermsModalProps = {
  open: boolean;
  onClose: () => void;
};

const TERMS_LINK_TOKEN = '__SUBSCRIPTION_TERMS_LINK__';

const applySubscriptionTermsPlaceholders = (
  content: string,
  labels: {
    subscribeButton: string;
    resubscribeButton: string;
    startTrialButton: string;
  },
): string =>
  content
    .split('{{subscribeButton}}')
    .join(labels.subscribeButton)
    .split('{{resubscribeButton}}')
    .join(labels.resubscribeButton)
    .split('{{startTrialButton}}')
    .join(labels.startTrialButton);

const renderLegalContent = (content: string): ReactElement[] =>
  content.split('\n').map((line, index) => {
    const key = `${index}-${line.slice(0, 12)}`;
    if (line.trim() === '') {
      return <div key={key} className="h-3" aria-hidden />;
    }
    if (line.startsWith('### ')) {
      return (
        <h3
          key={key}
          className="mt-5 text-base font-semibold text-gray-900 first:mt-0 dark:text-gray-100"
        >
          {line.replace(/^###\s+/, '')}
        </h3>
      );
    }
    return (
      <p key={key} className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
        {line}
      </p>
    );
  });

export const buildSubscriptionTermsConsentParts = (
  template: string,
  buttonLabel: string,
  termsLinkLabel: string,
): { before: string; after: string; termsLinkLabel: string } => {
  const withButton = template.split('{{buttonLabel}}').join(buttonLabel);
  const withToken = withButton.split('{{termsLink}}').join(TERMS_LINK_TOKEN);
  const [before = '', after = ''] = withToken.split(TERMS_LINK_TOKEN);
  return { before, after, termsLinkLabel };
};

export const SubscriptionTermsModal = ({
  open,
  onClose,
}: SubscriptionTermsModalProps): ReactElement | null => {
  const { t, language } = useLanguage();
  const titleId = useId();
  const [termsDocument, setTermsDocument] = useState<LegalDocumentPublic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoading(true);
      setError('');
      try {
        const response = await legalDocumentApi.getActive(
          'subscription_terms',
          resolveLegalDocumentLocale(language),
        );
        if (cancelled) {
          return;
        }
        if (response.success && response.data) {
          setTermsDocument(response.data);
          return;
        }
        setTermsDocument(null);
        setError(response.error ?? t('subscription.termsLoadError'));
      } catch (err) {
        if (cancelled) {
          return;
        }
        setTermsDocument(null);
        setError(err instanceof Error ? err.message : t('subscription.termsLoadError'));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [language, open, t]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const showEnglishContentNotice = usesEnglishLegalContent(language);
  const resolvedContent = termsDocument
    ? applySubscriptionTermsPlaceholders(termsDocument.content, {
        subscribeButton: t('subscription.subscribeButton'),
        resubscribeButton: t('subscription.resubscribeButton'),
        startTrialButton: t('subscription.startTrialButton'),
      })
    : '';

  return createPortal(
    <div className={MODAL_BACKDROP_CENTER_CLASS}>
      <button
        type="button"
        className={`absolute inset-0 ${MODAL_BACKDROP_SCRIM_CLASS}`}
        aria-label={t('common.close')}
        onClick={onClose}
        tabIndex={0}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900 sm:p-5"
      >
        <div className="flex shrink-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {termsDocument?.title ?? t('subscription.termsTitle')}
            </h2>
            {termsDocument ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Version: {termsDocument.version}
              </p>
            ) : null}
          </div>
          <ModalCircleCloseButton onClick={onClose} ariaLabel={t('common.close')} />
        </div>

        <div className="mt-4 min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-1">
          {showEnglishContentNotice ? (
            <p
              className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              role="status"
            >
              {t('legal.contentEnglishNotice')}
            </p>
          ) : null}
          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('common.loading')}
            </p>
          ) : null}
          {error ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : null}
          {!loading && !error && termsDocument ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-950/40">
              {renderLegalContent(resolvedContent)}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    globalThis.document.body,
  );
};
